import { Fragment, useState } from 'react';
import { FormIcon } from '../FormIcon.jsx';
import { FieldControl, FormBanner } from '../FormRenderer.jsx';
import { CATEGORIES, CHOICE_TYPES, LAYOUT_TYPES, PALETTE, blankField, duplicateField, paletteItem, slugKey, uniqueKey } from '../formSchema.js';
import { Check, ClearableSelect, Field, Toggle } from './controls.jsx';
import { SearchableSelect } from '../../components/SearchableSelect.jsx';

const NEW_PREFIX = 'new:';
const MOVE_PREFIX = 'move:';
const FILE_TYPES = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'pdf', 'ai', 'eps', 'psd', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'csv', 'txt', 'zip', 'mp4', 'mov'];

export function audienceForCategory(category, audience) {
  if (category === 'website_form') return 'website';
  if ((category === 'questionnaire' || category === 'client_request') && (audience === 'website' || audience === 'internal')) return 'client';
  return audience;
}

export function FormBuildTab({ draft, patch, patchSettings, options }) {
  const fields = draft.fields || [];
  const [selectedId, setSelectedId] = useState(null);
  const [dropIndex, setDropIndex] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const selected = fields.find((f) => f.id === selectedId) || null;

  const setFields = (updater) => patch((cur) => ({ fields: updater(cur.fields || []) }));

  const insertAt = (type, index) => {
    const field = blankField(type, fields);
    setFields((list) => {
      const next = [...list];
      next.splice(index == null ? next.length : index, 0, field);
      return next;
    });
    setSelectedId(field.id);
  };

  const addFromPalette = (type) => {
    const at = selected ? fields.findIndex((f) => f.id === selected.id) + 1 : fields.length;
    insertAt(type, at);
  };

  const moveTo = (id, index) => {
    setFields((list) => {
      const from = list.findIndex((f) => f.id === id);
      if (from < 0) return list;
      let to = index;
      if (from < to) to -= 1;
      if (from === to) return list;
      const next = [...list];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
  };

  const moveBy = (id, delta) => {
    const from = fields.findIndex((f) => f.id === id);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= fields.length) return;
    moveTo(id, delta > 0 ? to + 1 : to);
  };

  const updateField = (id, changes) => setFields((list) => list.map((f) => (f.id === id ? { ...f, ...changes } : f)));

  const removeField = (id) => {
    setFields((list) => list.filter((f) => f.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  const copyField = (field) => {
    const copy = duplicateField(field, fields);
    setFields((list) => {
      const next = [...list];
      next.splice(list.findIndex((f) => f.id === field.id) + 1, 0, copy);
      return next;
    });
    setSelectedId(copy.id);
  };

  const onDrop = (e) => {
    e.preventDefault();
    const data = e.dataTransfer.getData('text/plain');
    const index = dropIndex ?? fields.length;
    if (data.startsWith(NEW_PREFIX)) insertAt(data.slice(NEW_PREFIX.length), index);
    else if (data.startsWith(MOVE_PREFIX)) moveTo(data.slice(MOVE_PREFIX.length), index);
    setDropIndex(null);
    setDraggingId(null);
  };

  const overField = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setDropIndex(e.clientY > rect.top + rect.height / 2 ? index + 1 : index);
  };

  return (
    <div className="fm-build">
      <aside className="fm-card fm-palette">
        <h3>Add Fields</h3>
        <p>Drag and drop fields to build your form.</p>
        <ul>
          {PALETTE.map((item) => (
            <li key={item.type}>
              <button
                type="button"
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData('text/plain', NEW_PREFIX + item.type);
                  e.dataTransfer.effectAllowed = 'copy';
                }}
                onDragEnd={() => setDropIndex(null)}
                onClick={() => addFromPalette(item.type)}
                title={`Add ${item.label}`}
              >
                <span className="fm-palette-icon">
                  <FormIcon name={item.icon} size={16} />
                </span>
                <span className="fm-palette-label">{item.label}</span>
                <FormIcon name="grip" size={14} className="fm-grip" />
              </button>
            </li>
          ))}
        </ul>
      </aside>

      <section
        className={`fm-card fm-canvas${dropIndex !== null ? ' is-dropping' : ''}`}
        onDragOver={(e) => {
          e.preventDefault();
          if (e.target === e.currentTarget || e.target.classList?.contains('fm-canvas-end')) setDropIndex(fields.length);
        }}
        onDragLeave={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) setDropIndex(null);
        }}
        onDrop={onDrop}
        onClick={(e) => {
          if (e.target === e.currentTarget) setSelectedId(null);
        }}
      >
        {draft.settings.general.banner ? (
          <FormBanner title={draft.title} description={draft.description} />
        ) : (
          <div className="fm-canvas-title">
            <h2>{draft.title}</h2>
            {draft.description ? <p>{draft.description}</p> : null}
          </div>
        )}
        <div className="fm-canvas-body">
          {fields.map((field, index) => (
            <Fragment key={field.id}>
              {dropIndex === index ? <div className="fm-drop-line" /> : null}
              <div
                className={`fm-canvas-field${selectedId === field.id ? ' is-selected' : ''}${draggingId === field.id ? ' is-dragging' : ''}`}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData('text/plain', MOVE_PREFIX + field.id);
                  e.dataTransfer.effectAllowed = 'move';
                  setDraggingId(field.id);
                }}
                onDragEnd={() => {
                  setDraggingId(null);
                  setDropIndex(null);
                }}
                onDragOver={(e) => overField(e, index)}
                onClick={() => setSelectedId(field.id)}
                role="button"
                tabIndex={0}
                aria-label={`${paletteItem(field.type).label}: ${field.label}`}
                onKeyDown={(e) => {
                  if (e.target !== e.currentTarget) return;
                  if (e.key === 'Enter') setSelectedId(field.id);
                  if (e.key === 'Delete' || e.key === 'Backspace') removeField(field.id);
                  if (e.altKey && e.key === 'ArrowUp') moveBy(field.id, -1);
                  if (e.altKey && e.key === 'ArrowDown') moveBy(field.id, 1);
                }}
              >
                <span className="fm-canvas-grip" aria-hidden="true">
                  <FormIcon name="grip" size={14} />
                </span>
                <div className="fm-canvas-control">
                  <FieldControl field={field} value={field.type === 'checkbox' && field.options.length ? [] : ''} onChange={() => {}} disabled />
                </div>
                {selectedId === field.id ? (
                  <div className="fm-canvas-tools" onClick={(e) => e.stopPropagation()}>
                    <button type="button" className="fm-icon-btn" onClick={() => moveBy(field.id, -1)} disabled={index === 0} aria-label="Move up" title="Move up">
                      <FormIcon name="chevronDown" size={15} className="fm-flip" />
                    </button>
                    <button type="button" className="fm-icon-btn" onClick={() => moveBy(field.id, 1)} disabled={index === fields.length - 1} aria-label="Move down" title="Move down">
                      <FormIcon name="chevronDown" size={15} />
                    </button>
                    <button type="button" className="fm-icon-btn" onClick={() => copyField(field)} aria-label="Duplicate field" title="Duplicate">
                      <FormIcon name="copy" size={15} />
                    </button>
                    <button type="button" className="fm-icon-btn is-danger" onClick={() => removeField(field.id)} aria-label="Delete field" title="Delete">
                      <FormIcon name="trash" size={15} />
                    </button>
                  </div>
                ) : null}
              </div>
            </Fragment>
          ))}
          {dropIndex === fields.length ? <div className="fm-drop-line" /> : null}
          <div className="fm-canvas-end">{fields.length ? 'Drop a field here to add it at the end' : 'Drag fields from the left, or click one to add it'}</div>
          <button type="button" className="fm-btn fm-btn--blue fm-submit is-static" tabIndex={-1}>
            {draft.settings.general.submit_label || 'Submit'}
          </button>
        </div>
      </section>

      <aside className="fm-card fm-side">
        {selected ? (
          <FieldInspector
            key={selected.id}
            field={selected}
            fields={fields}
            onChange={(changes) => updateField(selected.id, changes)}
            onClose={() => setSelectedId(null)}
            onDelete={() => removeField(selected.id)}
            onDuplicate={() => copyField(selected)}
          />
        ) : (
          <QuickSettings draft={draft} patch={patch} patchSettings={patchSettings} options={options} />
        )}
      </aside>
    </div>
  );
}

function QuickSettings({ draft, patch, patchSettings, options }) {
  const projects = (options.projects || []).filter((p) => !draft.client_id || p.client_id === draft.client_id);
  const portal = draft.settings.portal;
  const clientFacing = draft.audience === 'client' || draft.audience === 'project';

  return (
    <div className="fm-side-body">
      <h3>
        Form Settings <span>(Quick)</span>
      </h3>
      <Field label="Form name">
        <input value={draft.title} onChange={(e) => patch({ title: e.target.value })} />
      </Field>
      <Field label="Type">
        <div className="fm-select-wrap">
          <SearchableSelect value={draft.category} onChange={(e) => patch({ category: e.target.value, audience: audienceForCategory(e.target.value, draft.audience) })}>
            {CATEGORIES.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </SearchableSelect>
          <FormIcon name="chevronDown" size={16} />
        </div>
      </Field>
      <Field label="Assign to client" optional>
        <ClearableSelect
          value={draft.client_id}
          placeholder="Select a client"
          options={options.clients || []}
          ariaLabel="Assign to client"
          onChange={(clientId) =>
            patch((cur) => ({
              client_id: clientId,
              project_id: clientId && cur.project_id && (options.projects || []).find((p) => p.id === cur.project_id)?.client_id === clientId ? cur.project_id : null,
              audience: clientId && (cur.audience === 'website' || cur.audience === 'internal') && cur.category !== 'template' ? 'client' : cur.audience,
            }))
          }
        />
      </Field>
      <Field label="Assign to project" optional>
        <ClearableSelect
          value={draft.project_id}
          placeholder={projects.length ? 'Select a project' : draft.client_id ? 'No projects for this client' : 'Select a project'}
          options={projects}
          disabled={!projects.length}
          ariaLabel="Assign to project"
          onChange={(projectId) =>
            patch(() => {
              const project = (options.projects || []).find((p) => p.id === projectId);
              return { project_id: projectId, client_id: project ? project.client_id : draft.client_id };
            })
          }
        />
      </Field>
      <Field label="Due date" optional>
        <div className="fm-input-icon">
          <FormIcon name="date" size={16} />
          <input type="date" value={draft.due_date || ''} onChange={(e) => patch({ due_date: e.target.value || null })} />
        </div>
      </Field>
      <div className="fm-side-checks">
        <Check checked={portal.show_in_portal} onChange={(v) => patchSettings('portal', { show_in_portal: v })} label={'Add to “We Need From You” (client portal)'} disabled={!clientFacing} />
        <Check checked={portal.notify_client} onChange={(v) => patchSettings('portal', { notify_client: v })} label="Send client notification" disabled={!clientFacing} />
        <Check checked={portal.remind_if_incomplete} onChange={(v) => patchSettings('portal', { remind_if_incomplete: v })} label="Remind if incomplete" disabled={!clientFacing || !draft.due_date} />
      </div>
      {!clientFacing ? <p className="fm-hint">Client portal options apply when the form is sent to a client or project (Settings → Form type).</p> : null}
      {clientFacing && !draft.client_id ? <p className="fm-hint is-warn">Pick a client before publishing.</p> : null}
    </div>
  );
}

function OptionsEditor({ field, onChange }) {
  const options = field.options || [];
  const set = (next) => onChange({ options: next });
  return (
    <div className="fm-options">
      <span className="fm-field-label">Options</span>
      {options.map((option, index) => (
        <div key={index} className="fm-option-row">
          <span className={`fm-option-mark fm-option-mark--${field.type}`} aria-hidden="true" />
          <input
            value={option}
            onChange={(e) => set(options.map((o, i) => (i === index ? e.target.value : o)))}
            onBlur={() => set(options.map((o) => o.trim()).filter((o, i, arr) => o !== '' && arr.indexOf(o) === i))}
            aria-label={`Option ${index + 1}`}
          />
          <button type="button" className="fm-icon-btn" onClick={() => set(options.filter((_, i) => i !== index))} aria-label={`Remove option ${index + 1}`}>
            <FormIcon name="x" size={14} />
          </button>
        </div>
      ))}
      <button type="button" className="fm-btn fm-btn--sm fm-btn--ghost" onClick={() => set([...options, `Option ${options.length + 1}`])}>
        <FormIcon name="plus" size={14} /> Add option
      </button>
      {field.type === 'checkbox' ? <small className="fm-hint">Remove all options for a single yes / no checkbox (e.g. “I agree to the terms”).</small> : null}
      {field.type !== 'checkbox' && options.length < 2 ? <small className="fm-hint is-warn">Add at least two options.</small> : null}
    </div>
  );
}

function FieldInspector({ field, fields, onChange, onClose, onDelete, onDuplicate }) {
  const item = paletteItem(field.type);
  const isLayout = LAYOUT_TYPES.includes(field.type);
  const singleCheckbox = field.type === 'checkbox' && !field.options.length;
  const [keyText, setKeyText] = useState(field.key);

  return (
    <div className="fm-side-body">
      <button type="button" className="fm-side-back" onClick={onClose}>
        <FormIcon name="chevronLeft" size={15} /> Form settings
      </button>
      <h3 className="fm-inspector-title">
        <span className="fm-palette-icon">
          <FormIcon name={item.icon} size={16} />
        </span>
        {item.label}
      </h3>

      {field.type === 'divider' ? <p className="fm-hint">A divider adds a visual break between sections. It has no settings.</p> : null}

      {field.type !== 'divider' && field.type !== 'paragraph' ? (
        <Field label={field.type === 'heading' ? 'Heading text' : 'Label'}>
          <input value={field.label} onChange={(e) => onChange({ label: e.target.value })} autoFocus />
        </Field>
      ) : null}

      {field.type === 'paragraph' ? (
        <Field label="Text">
          <textarea rows={5} value={field.text} onChange={(e) => onChange({ text: e.target.value })} />
        </Field>
      ) : null}

      {['text', 'textarea', 'email', 'phone', 'url', 'select'].includes(field.type) || singleCheckbox ? (
        <Field label={singleCheckbox ? 'Checkbox text' : 'Placeholder'}>
          <input value={field.placeholder} onChange={(e) => onChange({ placeholder: e.target.value })} placeholder={singleCheckbox ? 'Yes' : ''} />
        </Field>
      ) : null}

      {field.type !== 'divider' && field.type !== 'paragraph' ? (
        <Field label={field.type === 'heading' ? 'Subtitle' : 'Helper text'} optional>
          <input value={field.help} onChange={(e) => onChange({ help: e.target.value })} placeholder={field.type === 'heading' ? '' : 'Shown under the field'} />
        </Field>
      ) : null}

      {CHOICE_TYPES.includes(field.type) ? <OptionsEditor field={field} onChange={onChange} /> : null}

      {field.type === 'file' ? (
        <>
          <div className="fm-field">
            <span>Allowed file types</span>
            <div className="fm-ext-grid">
              {FILE_TYPES.map((ext) => {
                const on = field.accept.includes(ext);
                return (
                  <button
                    key={ext}
                    type="button"
                    className={`fm-ext${on ? ' is-on' : ''}`}
                    aria-pressed={on}
                    onClick={() => onChange({ accept: on ? field.accept.filter((x) => x !== ext) : [...field.accept, ext] })}
                  >
                    {ext.toUpperCase()}
                  </button>
                );
              })}
            </div>
            <small className="fm-hint">None selected = use the form-wide list from Settings → Advanced.</small>
          </div>
          <Field label="Max file size (MB)">
            <input type="number" min={1} max={50} value={field.max_mb || ''} onChange={(e) => onChange({ max_mb: Math.max(0, Math.min(50, Number(e.target.value) || 0)) })} />
          </Field>
          <Check checked={field.multiple} onChange={(v) => onChange({ multiple: v })} label="Allow multiple files (up to 10)" />
        </>
      ) : null}

      {!isLayout ? (
        <>
          <Toggle checked={field.required} onChange={(v) => onChange({ required: v })} label="Required" hint="Respondents must answer before submitting." />
          <Field label="Field key" hint="Used in CSV export, email templates ({{key}}) and integrations.">
            <input
              value={keyText}
              onChange={(e) => setKeyText(e.target.value)}
              onBlur={() => {
                const next = uniqueKey(slugKey(keyText) || field.label, fields, field.id);
                setKeyText(next);
                onChange({ key: next });
              }}
            />
          </Field>
        </>
      ) : null}

      <div className="fm-inspector-actions">
        <button type="button" className="fm-btn fm-btn--sm" onClick={onDuplicate}>
          <FormIcon name="copy" size={14} /> Duplicate
        </button>
        <button type="button" className="fm-btn fm-btn--sm fm-btn--outline-danger" onClick={onDelete}>
          <FormIcon name="trash" size={14} /> Delete field
        </button>
      </div>
    </div>
  );
}
